import { getSupabase } from '@/lib/supabase'
import { withAuthRecovery } from '@/lib/authRecovery'
import type {
  ContractAmendment,
  ContractAmendmentDebtPolicy,
  ContractAmendmentType,
} from '@/types'

function formatError(error: unknown): string {
  if (!error) return 'Unknown error'
  if (error instanceof Error) return error.message
  if (typeof error === 'object') {
    const e = error as { message?: string; details?: string; hint?: string; code?: string }
    return [e.message, e.details, e.hint, e.code].filter(Boolean).join(' | ') || JSON.stringify(error)
  }
  return String(error)
}

// Append-only журнал ДС-событий ДКК (contract_amendments; RLS SELECT грантнут authenticated).
export async function loadContractAmendments(contractId: number): Promise<ContractAmendment[]> {
  const supabase = getSupabase()
  const { data, error } = await withAuthRecovery(() =>
    (supabase as any)
      .from('contract_amendments')
      .select(
        'amendment_id,contract_id,amendment_type,effective_from,signed_at,activated_at,activated_by,new_entity_id,new_expected_end_date,debt_policy,note,doc_url,created_by,created_at'
      )
      .eq('contract_id', contractId)
      .order('effective_from', { ascending: true })
      .order('amendment_id', { ascending: true })
  )
  if (error) throw new Error(formatError(error))
  return (data ?? []) as ContractAmendment[]
}

export interface CreateContractAmendmentInput {
  contract_id: number
  amendment_type: ContractAmendmentType
  effective_from: string
  new_entity_id?: number | null
  new_expected_end_date?: string | null
  debt_policy?: ContractAmendmentDebtPolicy | null
  note?: string | null
  doc_url?: string | null
  activated?: boolean
}

export interface UpdateContractAmendmentInput {
  amendment_id: number
  effective_from: string
  new_entity_id?: number | null
  new_expected_end_date?: string | null
  debt_policy?: ContractAmendmentDebtPolicy | null
  note?: string | null
  doc_url?: string | null
}

// Создать ДС-событие. Правила payload-полей по типу проверяет сам RPC (см. миграцию).
export async function createContractAmendment(input: CreateContractAmendmentInput): Promise<number> {
  const supabase = getSupabase()
  const { data, error } = await withAuthRecovery(() =>
    (supabase as any).rpc('create_contract_amendment', {
      p_contract_id: input.contract_id,
      p_amendment_type: input.amendment_type,
      p_effective_from: input.effective_from,
      p_new_entity_id: input.new_entity_id ?? null,
      p_new_expected_end_date: input.new_expected_end_date ?? null,
      p_debt_policy: input.debt_policy ?? null,
      p_note: input.note ?? null,
      p_doc_url: input.doc_url ?? null,
      p_activated: input.activated ?? true,
    })
  )
  if (error) throw new Error(formatError(error))
  return data as number
}

/**
 * Активация ДС = отметка «франчайзи подписал». До неё ДС не влияет ни на
 * юридическое состояние договора, ни на сторону, ни на условия роялти: его нет
 * ни в contract_legal_state_v, ни в contract_party_intervals. Активация
 * расторжения именно в этот момент закрывает условия роялти.
 */
export async function activateContractAmendment(
  amendmentId: number,
  signedAt: string | null
): Promise<void> {
  const supabase = getSupabase()
  const { error } = await withAuthRecovery(() =>
    (supabase as any).rpc('activate_contract_amendment', {
      p_amendment_id: amendmentId,
      p_signed_at: signedAt || null,
    })
  )
  if (error) throw new Error(formatError(error))
}

// Исправить ошибочно заведённое ДС. Тип события остаётся прежним; payload проверяет RPC.
export async function updateContractAmendment(input: UpdateContractAmendmentInput): Promise<ContractAmendment> {
  const supabase = getSupabase()
  const { data, error } = await withAuthRecovery(() =>
    (supabase as any).rpc('update_contract_amendment', {
      p_amendment_id: input.amendment_id,
      p_effective_from: input.effective_from,
      p_new_entity_id: input.new_entity_id ?? null,
      p_new_expected_end_date: input.new_expected_end_date ?? null,
      p_debt_policy: input.debt_policy ?? null,
      p_note: input.note ?? null,
      p_doc_url: input.doc_url ?? null,
    })
  )
  if (error) throw new Error(formatError(error))
  return data as ContractAmendment
}

export async function deleteContractAmendment(amendmentId: number): Promise<void> {
  const supabase = getSupabase()
  const { error } = await withAuthRecovery(() =>
    (supabase as any).rpc('delete_contract_amendment', {
      p_amendment_id: amendmentId,
    })
  )
  if (error) throw new Error(formatError(error))
}

// Связать юрлицо с партнёром (нужно перед ENTITY_CHANGE на «чужое» юрлицо). Идемпотентен.
export async function addPartnerEntityLink(
  partnerNumber: number,
  entityId: number,
  note?: string | null
): Promise<number> {
  const supabase = getSupabase()
  const { data, error } = await withAuthRecovery(() =>
    (supabase as any).rpc('add_partner_entity_link', {
      p_partner_number: partnerNumber,
      p_entity_id: entityId,
      p_note: note ?? null,
    })
  )
  if (error) throw new Error(formatError(error))
  return data as number
}
